import React, { useState, useEffect } from 'react';
import { apiService, CreateTransactionRequest } from '../services/api';
import ConfirmModal from './ConfirmModal';
import { useNotifications } from './NotificationSystem';
import { handleApiError } from '../utils/errorHandler';
import './RecurringTransactions.css';

interface RecurringTransaction {
  id: number;
  description: string;
  amount: number;
  type: number; // 1=Income, 2=Expense, 3=Transfer
  category: string;
  accountId: number;
  accountName: string;
  frequency: 'daily' | 'weekly' | 'monthly' | 'yearly';
  interval: number; // Every X days/weeks/months/years
  startDate: string;
  endDate?: string;
  nextDueDate: string;
  isActive: boolean;
  lastExecuted?: string;
  totalExecutions: number;
}

const RecurringTransactions: React.FC = () => {
  const [recurringTransactions, setRecurringTransactions] = useState<RecurringTransaction[]>([]);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState<RecurringTransaction | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; transactionId: number | null }>({
    isOpen: false,
    transactionId: null
  });
  const { showSuccess, showError } = useNotifications();
  const [formData, setFormData] = useState({
    description: '',
    amount: 0,
    type: 2, // 1=Income, 2=Expense, 3=Transfer
    category: '',
    accountId: 1,
    frequency: 'monthly' as 'daily' | 'weekly' | 'monthly' | 'yearly',
    interval: 1,
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    isActive: true
  });

  useEffect(() => {
    // CRITICAL: Don't make API calls if not authenticated
    if (!apiService.isAuthenticated()) {
      console.warn('RecurringTransactions: Not authenticated, skipping API calls');
      setLoading(false);
      return;
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    // CRITICAL: Don't make API calls if not authenticated
    if (!apiService.isAuthenticated()) {
      console.warn('RecurringTransactions: Not authenticated, skipping fetchData');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      // For now, we'll use mock data since we don't have the API endpoint yet
      const mockRecurringTransactions: RecurringTransaction[] = [
        {
          id: 1,
          description: 'Monthly Rent',
          amount: 15000,
          type: 2,
          category: 'Housing',
          accountId: 1,
          accountName: 'Bank Account',
          frequency: 'monthly',
          interval: 1,
          startDate: '2024-01-01',
          nextDueDate: '2024-12-01',
          isActive: true,
          lastExecuted: '2024-11-01',
          totalExecutions: 11
        },
        {
          id: 2,
          description: 'Netflix Subscription',
          amount: 199,
          type: 2,
          category: 'Entertainment',
          accountId: 2,
          accountName: 'Credit Card',
          frequency: 'monthly',
          interval: 1,
          startDate: '2024-06-15',
          nextDueDate: '2024-12-15',
          isActive: true,
          lastExecuted: '2024-11-15',
          totalExecutions: 5
        },
        {
          id: 3,
          description: 'Salary',
          amount: 50000,
          type: 1,
          category: 'Income',
          accountId: 1,
          accountName: 'Bank Account',
          frequency: 'monthly',
          interval: 1,
          startDate: '2024-01-01',
          nextDueDate: '2024-12-01',
          isActive: true,
          lastExecuted: '2024-11-01',
          totalExecutions: 11
        }
      ];

      setRecurringTransactions(mockRecurringTransactions);
      
      // Fetch accounts for dropdown
      const stats = await apiService.getDashboardStats();
      setAccounts(stats.accountBalances);
      
      if (stats.accountBalances.length > 0) {
        setFormData(prev => ({ ...prev, accountId: stats.accountBalances[0].id }));
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // For now, we'll add to the mock data
      const newRecurringTransaction: RecurringTransaction = {
        id: recurringTransactions.length + 1,
        description: formData.description,
        amount: formData.amount,
        type: formData.type,
        category: formData.category,
        accountId: formData.accountId,
        accountName: accounts.find(acc => acc.id === formData.accountId)?.name || 'Unknown',
        frequency: formData.frequency,
        interval: formData.interval,
        startDate: formData.startDate,
        endDate: formData.endDate || undefined,
        nextDueDate: calculateNextDueDate(formData.startDate, formData.frequency, formData.interval),
        isActive: formData.isActive,
        totalExecutions: 0
      };

      if (editingTransaction) {
        setRecurringTransactions(prev => 
          prev.map(t => t.id === editingTransaction.id ? { ...newRecurringTransaction, id: editingTransaction.id } : t)
        );
      } else {
        setRecurringTransactions(prev => [...prev, newRecurringTransaction]);
      }

      setShowAddForm(false);
      setEditingTransaction(null);
      setFormData({
        description: '',
        amount: 0,
        type: 2,
        category: '',
        accountId: accounts.length > 0 ? accounts[0].id : 1,
        frequency: 'monthly',
        interval: 1,
        startDate: new Date().toISOString().split('T')[0],
        endDate: '',
        isActive: true
      });
    } catch (error) {
      console.error('Error saving recurring transaction:', error);
    }
  };

  const calculateNextDueDate = (startDate: string, frequency: string, interval: number): string => {
    const start = new Date(startDate);
    const now = new Date();
    
    let nextDate = new Date(start);
    
    while (nextDate <= now) {
      switch (frequency) {
        case 'daily':
          nextDate.setDate(nextDate.getDate() + interval);
          break;
        case 'weekly':
          nextDate.setDate(nextDate.getDate() + (7 * interval));
          break;
        case 'monthly':
          nextDate.setMonth(nextDate.getMonth() + interval);
          break;
        case 'yearly':
          nextDate.setFullYear(nextDate.getFullYear() + interval);
          break;
      }
    }
    
    return nextDate.toISOString().split('T')[0];
  };

  const handleEdit = (transaction: RecurringTransaction) => {
    setEditingTransaction(transaction);
    setFormData({
      description: transaction.description,
      amount: transaction.amount,
      type: transaction.type,
      category: transaction.category,
      accountId: transaction.accountId,
      frequency: transaction.frequency,
      interval: transaction.interval,
      startDate: transaction.startDate,
      endDate: transaction.endDate || '',
      isActive: transaction.isActive
    });
    setShowAddForm(true);
  };

  const handleDeleteClick = (id: number) => {
    setDeleteConfirm({ isOpen: true, transactionId: id });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.transactionId) return;
    setRecurringTransactions(prev => prev.filter(t => t.id !== deleteConfirm.transactionId));
    setDeleteConfirm({ isOpen: false, transactionId: null });
  };

  const handleToggleActive = async (id: number) => {
    setRecurringTransactions(prev => 
      prev.map(t => t.id === id ? { ...t, isActive: !t.isActive } : t)
    );
  };

  const executeTransaction = async (recurringTransaction: RecurringTransaction) => {
    try {
      const transactionData: CreateTransactionRequest = {
        entryDate: new Date().toISOString().split('T')[0],
        accountId: recurringTransaction.accountId,
        description: recurringTransaction.description,
        amount: recurringTransaction.amount,
        type: recurringTransaction.type,
        category: recurringTransaction.category,
        narration: `Recurring transaction - ${recurringTransaction.frequency}`,
        reference: `REC-${recurringTransaction.id}`
      };

      await apiService.createTransaction(transactionData);
      
      // Update the recurring transaction
      const nextDueDate = calculateNextDueDate(
        recurringTransaction.nextDueDate, 
        recurringTransaction.frequency, 
        recurringTransaction.interval
      );
      
      setRecurringTransactions(prev => 
        prev.map(t => t.id === recurringTransaction.id ? {
          ...t,
          lastExecuted: new Date().toISOString().split('T')[0],
          nextDueDate,
          totalExecutions: t.totalExecutions + 1
        } : t)
      );
      
      showSuccess('Transaction Executed', 'Transaction executed successfully!');
    } catch (error) {
      const appError = handleApiError(error);
      showError('Execution Error', appError.message);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  const getFrequencyText = (frequency: string, interval: number) => {
    if (interval === 1) {
      return `Every ${frequency}`;
    }
    return `Every ${interval} ${frequency}s`;
  };

  const getStatusColor = (isActive: boolean, nextDueDate: string) => {
    if (!isActive) return '#6b7280';
    
    const daysUntilDue = Math.ceil((new Date(nextDueDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
    
    if (daysUntilDue < 0) return '#ef4444'; // Overdue - red
    if (daysUntilDue <= 3) return '#f59e0b'; // Due soon - yellow
    return '#10b981'; // On track - green
  };

  if (loading) {
    return <div className="recurring-transactions-loading">Loading recurring transactions...</div>;
  }

  return (
    <div className="recurring-transactions">
      <ConfirmModal
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, transactionId: null })}
        onConfirm={handleDeleteConfirm}
        title="Delete Recurring Transaction"
        message="Are you sure you want to delete this recurring transaction? This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        confirmVariant="danger"
      />
      
      <div className="recurring-header">
        <h1>Recurring Transactions</h1>
        <button 
          className="add-recurring-btn"
          onClick={() => {
            setShowAddForm(true);
            setEditingTransaction(null);
            setFormData({
              description: '',
              amount: 0,
              type: 2,
              category: '',
              accountId: accounts.length > 0 ? accounts[0].id : 1,
              frequency: 'monthly',
              interval: 1,
              startDate: new Date().toISOString().split('T')[0],
              endDate: '',
              isActive: true
            });
          }}
        >
          <span>+</span>
          Add Recurring Transaction
        </button>
      </div>

      {showAddForm && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>{editingTransaction ? 'Edit Recurring Transaction' : 'Add New Recurring Transaction'}</h2>
            <button className="modal-close-btn" onClick={() => setShowAddForm(false)}>
              ×
            </button>
            
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Description</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="e.g., Monthly Rent, Netflix Subscription"
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Type</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: parseInt(e.target.value) })}
                    required
                  >
                    <option value={2}>Expense</option>
                    <option value={1}>Income</option>
                    <option value={3}>Transfer</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Amount</label>
                  <input
                    type="number"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Category</label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    placeholder="e.g., Housing, Entertainment, Food"
                  />
                </div>
                <div className="form-group">
                  <label>Account</label>
                  <select
                    value={formData.accountId}
                    onChange={(e) => setFormData({ ...formData, accountId: parseInt(e.target.value) })}
                    required
                  >
                    {accounts.map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.name} ({account.accountType})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Frequency</label>
                  <select
                    value={formData.frequency}
                    onChange={(e) => setFormData({ ...formData, frequency: e.target.value as any })}
                    required
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Interval</label>
                  <input
                    type="number"
                    value={formData.interval}
                    onChange={(e) => setFormData({ ...formData, interval: parseInt(e.target.value) || 1 })}
                    min="1"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Start Date</label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>End Date (Optional)</label>
                  <input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-group checkbox-group">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
                <label htmlFor="isActive">Active</label>
              </div>

              <button type="submit" className="btn-primary">
                {editingTransaction ? 'Update Recurring Transaction' : 'Add Recurring Transaction'}
              </button>
            </form>
          </div>
        </div>
      )}

      <div className="recurring-transactions-list">
        {recurringTransactions.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">🔄</div>
            <h2>No Recurring Transactions</h2>
            <p>Set up recurring transactions for bills, subscriptions, and regular payments</p>
          </div>
        ) : (
          recurringTransactions.map((transaction) => (
            <div key={transaction.id} className="recurring-transaction-card">
              <div className="transaction-icon">
                {transaction.type === 1 ? '📈' : transaction.type === 2 ? '📉' : '🔄'}
              </div>
              <div className="transaction-details">
                <h3>{transaction.description}</h3>
                <p className="transaction-category">{transaction.category} • {transaction.accountName}</p>
                <p className="transaction-frequency">
                  {getFrequencyText(transaction.frequency, transaction.interval)}
                </p>
                <p className="transaction-next-due">
                  Next due: {formatDate(transaction.nextDueDate)}
                </p>
                {transaction.lastExecuted && (
                  <p className="transaction-last-executed">
                    Last executed: {formatDate(transaction.lastExecuted)}
                  </p>
                )}
                <p className="transaction-executions">
                  Executed {transaction.totalExecutions} times
                </p>
              </div>
              <div className="transaction-actions">
                <button 
                  className="action-btn execute-btn" 
                  onClick={() => executeTransaction(transaction)}
                  title="Execute now"
                >
                  ⚡
                </button>
                <button 
                  className="action-btn edit-btn" 
                  onClick={() => handleEdit(transaction)}
                  title="Edit transaction"
                >
                  ✏️
                </button>
                <button 
                  className="action-btn toggle-btn" 
                  onClick={() => handleToggleActive(transaction.id)}
                  title={transaction.isActive ? 'Deactivate' : 'Activate'}
                >
                  {transaction.isActive ? '⏸️' : '▶️'}
                </button>
                <button 
                  className="action-btn delete-btn" 
                  onClick={() => handleDeleteClick(transaction.id)}
                  title="Delete transaction"
                >
                  🗑️
                </button>
              </div>
              <div 
                className="transaction-amount" 
                style={{ color: getStatusColor(transaction.isActive, transaction.nextDueDate) }}
              >
                {transaction.type === 1 ? '+' : transaction.type === 2 ? '-' : '↔'}{formatCurrency(transaction.amount)}
              </div>
              <div 
                className="status-indicator"
                style={{ backgroundColor: getStatusColor(transaction.isActive, transaction.nextDueDate) }}
              ></div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default RecurringTransactions;
